import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FiStar, FiMessageCircle, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import './Testimonials.css';

const Testimonials = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Détecter si on est sur mobile
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // État pour mettre en pause l'auto-play
  const [isPaused, setIsPaused] = useState(false);
  const [pauseTimeout, setPauseTimeout] = useState(null);

  // Fonction pour reprendre avec délai
  const resumeWithDelay = () => {
    if (pauseTimeout) {
      clearTimeout(pauseTimeout);
    }
    const timeout = setTimeout(() => {
      setIsPaused(false);
    }, 2000); // Attendre 2 secondes après avoir relâché
    setPauseTimeout(timeout);
  };

  // Auto-play du carrousel sur mobile (avec pause)
  useEffect(() => {
    if (isMobile && !isPaused) {
      const interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % testimonials.length);
      }, 4000);
      
      return () => clearInterval(interval);
    }
  }, [isMobile, isPaused]);

  // Nettoyer le timeout au démontage
  useEffect(() => {
    return () => {
      if (pauseTimeout) {
        clearTimeout(pauseTimeout);
      }
    };
  }, [pauseTimeout]);

  const testimonials = [
    {
      id: 1,
      name: 'Marie & Antoine Dubois',
      event: 'Mariage',
      rating: 5,
      text: 'Service exceptionnel pour notre mariage ! La limousine était magnifique et le chauffeur d\'une courtoisie parfaite. Nos invités étaient impressionnés. Un grand merci à toute l\'équipe VIP Limousine Paris.',
      date: 'Septembre 2024'
    },
    {
      id: 2,
      name: 'Jean-Pierre Martin',
      event: 'Transport d\'affaires',
      rating: 5,
      text: 'Ponctualité irréprochable et véhicule impeccable. J\'utilise régulièrement leurs services pour mes déplacements professionnels. La Mercedes Classe S est parfaite pour travailler en route.',
      date: 'Octobre 2024'
    },
    {
      id: 3,
      name: 'Sophie Laurent',
      event: 'Transfert aéroport',
      rating: 5,
      text: 'Transfert CDG parfait ! Chauffeur à l\'heure, véhicule confortable et tarif transparent. Plus jamais de stress pour les départs en voyage. Je recommande vivement.',
      date: 'Novembre 2024'
    },
    {
      id: 4,
      name: 'Alexandre & Camille',
      event: 'Demande en mariage',
      rating: 5,
      text: 'Moment magique grâce à VIP Limousine Paris ! La Rolls Royce pour ma demande en mariage était un rêve. Service discret et professionnel. Ma fiancée était aux anges !',
      date: 'Août 2024'
    },
    {
      id: 5,
      name: 'Corporate Events SARL',
      event: 'Événement d\'entreprise',
      rating: 5,
      text: 'Partenaire de confiance pour nos événements corporate. Flotte variée, chauffeurs professionnels et organisation parfaite. Nos clients VIP sont toujours satisfaits.',
      date: 'Septembre 2024'
    },
    {
      id: 6,
      name: 'Famille Rodriguez',
      event: 'Tour de Paris',
      rating: 5,
      text: 'Découverte de Paris en limousine pour nos 25 ans de mariage. Guide passionnant, véhicule somptueux et service aux petits soins. Une expérience inoubliable !',
      date: 'Octobre 2024'
    }
  ];

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <FiStar 
        key={index} 
        className={index < rating ? 'star filled' : 'star'} 
      />
    ));
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const reviewSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://limousine-vip-paris.fr/#business",
    "name": "VIP Limousine Paris",
    "url": "https://limousine-vip-paris.fr/",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "bestRating": "5",
      "worstRating": "1",
      "ratingCount": 127,
      "reviewCount": 127
    },
    "review": testimonials.map((t) => ({
      "@type": "Review",
      "author": { "@type": "Person", "name": t.name },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": t.rating,
        "bestRating": 5,
        "worstRating": 1
      },
      "reviewBody": t.text,
      "itemReviewed": {
        "@type": "Service",
        "name": t.event,
        "provider": { "@type": "LocalBusiness", "name": "VIP Limousine Paris" }
      }
    }))
  };

  return (
    <section className="testimonials section-padding">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(reviewSchema)}</script>
      </Helmet>
      <div className="container">
        <div className="section-header text-center">
          <span className="section-badge">Témoignages</span>
          <h2 className="section-title">
            Nos Clients
            <span className="title-accent">Témoignent</span>
          </h2>
          <p className="section-description">
            La satisfaction de nos clients est notre plus belle récompense. 
            Découvrez leurs expériences authentiques avec nos services de transport de luxe.
          </p>
        </div>

        <div className="testimonials-stats">
          <div className="stat">
            <span className="stat-number">4.9/5</span>
            <span className="stat-label">Note moyenne</span>
            <div className="stat-stars">
              {renderStars(5)}
            </div>
          </div>
          <div className="stat">
            <span className="stat-number">500+</span>
            <span className="stat-label">Avis clients</span>
          </div>
          <div className="stat">
            <span className="stat-number">98%</span>
            <span className="stat-label">Recommandations</span>
          </div>
        </div>

        <div className={`testimonials-container ${isMobile ? 'mobile-carousel' : ''}`}>
          {isMobile ? (
            // Carrousel mobile
            <div className="carousel-wrapper">
              {isPaused && (
                <div className="pause-indicator">
                  ⏸️ Lecture en pause
                </div>
              )}
              <button 
                className="carousel-btn carousel-btn-prev"
                onClick={prevSlide}
                aria-label="Témoignage précédent"
              >
                <FiChevronLeft />
              </button>
              
              <div className="carousel-track">
                <div 
                  className="testimonials-slider"
                  style={{
                    transform: `translateX(-${currentSlide * 100}%)`,
                    transition: 'transform 0.5s ease-in-out'
                  }}
                >
                  {testimonials.map((testimonial, index) => (
                    <div 
                      key={testimonial.id} 
                      className="testimonial-slide"
                      onTouchStart={() => setIsPaused(true)}
                      onTouchEnd={resumeWithDelay}
                      onMouseEnter={() => setIsPaused(true)}
                      onMouseLeave={resumeWithDelay}
                    >
                      <div className="testimonial-card mobile-card">
                        <div className="testimonial-header">
                          <FiMessageCircle className="quote-icon" />
                          <div className="rating">
                            {renderStars(testimonial.rating)}
                          </div>
                        </div>
                        
                        <div className="testimonial-content">
                          <p className="testimonial-text">"{testimonial.text}"</p>
                        </div>
                        
                        <div className="testimonial-footer">
                          <div className="client-info">
                            <h4 className="client-name">{testimonial.name}</h4>
                            <span className="event-type">{testimonial.event}</span>
                            <span className="date">{testimonial.date}</span>
                          </div>
                          <div className="testimonial-badge">
                            <FiStar className="badge-star" />
                            <span>Vérifié</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <button 
                className="carousel-btn carousel-btn-next"
                onClick={nextSlide}
                aria-label="Témoignage suivant"
              >
                <FiChevronRight />
              </button>
              
              {/* Indicateurs de slides */}
              <div className="carousel-indicators">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    className={`indicator ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Aller au témoignage ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          ) : (
            // Grille desktop (original)
            <div className="testimonials-grid">
              {testimonials.map((testimonial, index) => (
                <div 
                  key={testimonial.id} 
                  className="testimonial-card"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="testimonial-header">
                    <FiMessageCircle className="quote-icon" />
                    <div className="rating">
                      {renderStars(testimonial.rating)}
                    </div>
                  </div>
                  
                  <div className="testimonial-content">
                    <p className="testimonial-text">"{testimonial.text}"</p>
                  </div>
                  
                  <div className="testimonial-footer">
                    <div className="client-info">
                      <h4 className="client-name">{testimonial.name}</h4>
                      <span className="event-type">{testimonial.event}</span>
                      <span className="date">{testimonial.date}</span>
                    </div>
                    <div className="testimonial-badge">
                      <FiStar className="badge-star" />
                      <span>Vérifié</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="testimonials-cta">
          <div className="cta-content">
            <h3>Rejoignez nos clients satisfaits</h3>
            <p>Faites l'expérience du service VIP Limousine Paris et découvrez pourquoi nos clients nous font confiance.</p>
            <div className="cta-actions">
              <button className="btn btn-primary">Réserver maintenant</button>
              <button className="btn btn-outline">Voir plus d'avis</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
